/**
 * Status cell content for a single unit row. Lazy-loads the unit's status when the
 * row renders (i.e. when the property card is expanded) and shows a one-line skeleton
 * while the request is in flight.
 */
import { Paragraph, Tag } from '@zillow/constellation-10';

import { usePropertyTableStatus } from '../../../hooks/queries/propertyTable';
import { toTagTone } from '../../../utils/statusTagUtils';

import { UNIT_STATUS_LABELS } from '../../../constants/PropertiesListConstants';

import SkeletonCell from './SkeletonCell';

import type { ListingAlias } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  listingAlias: ListingAlias;
};

const StatusCell = ({ listingAlias }: Props) => {
  const { data, isFetching } = usePropertyTableStatus(listingAlias);

  if (isFetching) {
    return <SkeletonCell loadingWidth="72px" />;
  }

  if (!data) {
    return null;
  }

  const tone = toTagTone(data.tagAppearance);

  return (
    <>
      <Tag size="sm" tone={tone}>
        {UNIT_STATUS_LABELS[data.status] ?? data.status}
      </Tag>
      {data.description && (
        <Paragraph textStyle="body-xs" css={{ color: data.isCritical ? 'text.feedback.critical.hero' : 'text.subtle' }}>
          {data.description}
        </Paragraph>
      )}
    </>
  );
};

export default StatusCell;
